import { useEffect, useRef } from "react";
import * as THREE from "three";

/* Brand palette (mirrors Homepage.js tokens) */
const ORANGE = 0xee7c3b;
const INDIGO = 0x3d4ead;
const FOREST = 0x2f6b4f;
const PEACH = 0xf4ae59;
const CREAM = 0xfdf8f0;
const MINT = 0xbfdfce;

function letterTexture(letter, bgHex, fgHex) {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = bgHex;
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = "rgba(24,22,17,0.14)";
  ctx.lineWidth = 10;
  ctx.strokeRect(5, 5, size - 10, size - 10);

  ctx.fillStyle = fgHex;
  ctx.font = "800 150px 'Baloo 2', 'Trebuchet MS', sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(letter, size / 2, size / 2 + 10);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeBook(width, height, depth, coverColor, pageColor) {
  const group = new THREE.Group();

  // pages block
  const pagesGeo = new THREE.BoxGeometry(width * 0.96, height * 0.72, depth * 0.94);
  const pagesMat = new THREE.MeshStandardMaterial({
    color: pageColor,
    roughness: 0.9,
  });
  const pages = new THREE.Mesh(pagesGeo, pagesMat);
  group.add(pages);

  // two covers slightly larger than pages
  const coverGeo = new THREE.BoxGeometry(width, height * 0.16, depth);
  const coverMat = new THREE.MeshStandardMaterial({
    color: coverColor,
    roughness: 0.55,
  });
  const topCover = new THREE.Mesh(coverGeo, coverMat);
  topCover.position.y = height * 0.42;
  group.add(topCover);

  const bottomCover = new THREE.Mesh(coverGeo, coverMat);
  bottomCover.position.y = -height * 0.42;
  group.add(bottomCover);

  // spine
  const spineGeo = new THREE.BoxGeometry(width * 0.1, height * 0.98, depth);
  const spineMat = new THREE.MeshStandardMaterial({
    color: coverColor,
    roughness: 0.45,
  });
  const spine = new THREE.Mesh(spineGeo, spineMat);
  spine.position.x = -width * 0.46;
  group.add(spine);

  // ribbon bookmark
  const ribbonGeo = new THREE.BoxGeometry(depth * 0.12, height * 0.55, 0.02);
  const ribbonMat = new THREE.MeshStandardMaterial({
    color: PEACH,
    roughness: 0.7,
  });
  const ribbon = new THREE.Mesh(ribbonGeo, ribbonMat);
  ribbon.rotation.x = Math.PI / 2;
  ribbon.position.set(0.05, height * 0.35, depth * 0.4);
  group.add(ribbon);

  return group;
}

function makePencil() {
  const group = new THREE.Group();
  const len = 1.6;

  const bodyMat = new THREE.MeshStandardMaterial({
    color: PEACH,
    roughness: 0.6,
  });
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, len, 20), bodyMat);
  group.add(body);

  const woodMat = new THREE.MeshStandardMaterial({ color: 0xe8d5a8, roughness: 0.85 });
  const wood = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.02, 0.32, 20), woodMat);
  wood.position.y = len / 2 + 0.16;
  group.add(wood);

  const leadMat = new THREE.MeshStandardMaterial({ color: 0x181611, roughness: 0.5 });
  const lead = new THREE.Mesh(new THREE.ConeGeometry(0.02, 0.09, 16), leadMat);
  lead.position.y = len / 2 + 0.36;
  group.add(lead);

  const eraserMat = new THREE.MeshStandardMaterial({ color: 0xf2a6a6, roughness: 0.8 });
  const eraser = new THREE.Mesh(new THREE.CylinderGeometry(0.115, 0.115, 0.16, 20), eraserMat);
  eraser.position.y = -len / 2 - 0.08;
  group.add(eraser);

  const bandMat = new THREE.MeshStandardMaterial({ color: 0xbfc7d8, roughness: 0.35, metalness: 0.5 });
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.118, 0.118, 0.06, 20), bandMat);
  band.position.y = -len / 2 + 0.04;
  group.add(band);

  return group;
}

export default function Hero3D() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let width = mount.clientWidth || 800;
    let height = mount.clientHeight || 600;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.display = "block";

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(43, width / height, 0.1, 100);
    camera.position.set(0, 0.2, 10.5);

    // Soft studio lighting
    scene.add(new THREE.AmbientLight(0xffffff, 1.15));
    const key = new THREE.DirectionalLight(0xfff4e0, 2.2);
    key.position.set(4, 6, 6);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xdceee3, 0.9);
    fill.position.set(-5, -2, 4);
    scene.add(fill);

    const world = new THREE.Group();
    scene.add(world);

    /* ---- Book stack ---- */
    const bookColors = [ORANGE, INDIGO, FOREST];
    const stack = new THREE.Group();
    const bookH = 0.34;
    for (let i = 0; i < 3; i += 1) {
      const w = 2.1 - i * 0.22;
      const d = 1.5 - i * 0.18;
      const book = makeBook(w, bookH, d, bookColors[i], CREAM);
      book.position.y = i * (bookH * 1.08) - bookH * 1.05;
      book.rotation.y = (i % 2 === 0 ? 1 : -1) * 0.12 * (i + 1);
      stack.add(book);
    }
    stack.position.set(-3.1, -0.9, 0);
    stack.userData = { baseY: -0.9, phase: 0.4, bobSpeed: 0.55, rotSpeed: 0.0012 };
    world.add(stack);

    /* ---- Pencil ---- */
    const pencil = makePencil();
    pencil.rotation.z = Math.PI / 3.1;
    pencil.position.set(3.1, 1.9, -0.4);
    pencil.userData = { baseY: 1.9, phase: 2.1, bobSpeed: 0.7, rotSpeed: 0.004 };
    world.add(pencil);

    /* ---- A/B/C/D answer cubes ---- */
    const cubeDefs = [
      { letter: "A", bg: "#EE7C3B", fg: "#FFFFFF", x: -3.4, y: -2.6, z: 0.8, s: 0.62 },
      { letter: "B", bg: "#3D4EAD", fg: "#FFFFFF", x: -1.2, y: -3.0, z: 1.0, s: 0.52 },
      { letter: "C", bg: "#2F6B4F", fg: "#FFFFFF", x: 1.1, y: -2.9, z: 0.7, s: 0.58 },
      { letter: "D", bg: "#181611", fg: "#FDF8F0", x: 3.0, y: -2.7, z: 0.4, s: 0.5 },
    ];
    const cubes = [];
    cubeDefs.forEach((def, idx) => {
      const mat = new THREE.MeshStandardMaterial({
        map: letterTexture(def.letter, def.bg, def.fg),
        roughness: 0.45,
      });
      const cube = new THREE.Mesh(new THREE.BoxGeometry(def.s, def.s, def.s), mat);
      cube.position.set(def.x, def.y, def.z);
      cube.userData = {
        baseY: def.y,
        phase: idx * 1.4,
        bobSpeed: 0.8 + idx * 0.1,
        rotSpeed: 0.006 + idx * 0.002,
        tumbleAxis: new THREE.Vector3(
          Math.sin(idx * 2.1),
          Math.cos(idx * 1.3),
          Math.sin(idx * 0.7),
        ).normalize(),
      };
      world.add(cube);
      cubes.push(cube);
    });

    /* ---- Atom — nucleus cluster + 3 tilted orbits with travelling electrons ---- */
    const atom = new THREE.Group();

    const nucleusMats = [
      new THREE.MeshStandardMaterial({ color: ORANGE, roughness: 0.3 }),
      new THREE.MeshStandardMaterial({ color: INDIGO, roughness: 0.3 }),
      new THREE.MeshStandardMaterial({ color: PEACH, roughness: 0.35 }),
      new THREE.MeshStandardMaterial({ color: FOREST, roughness: 0.35 }),
    ];
    const nucleusOffsets = [
      [0, 0, 0, 0.24],
      [0.16, 0.1, 0.05, 0.15],
      [-0.14, -0.08, 0.08, 0.13],
      [0.02, -0.15, -0.1, 0.12],
    ];
    nucleusOffsets.forEach(([x, y, z, r], i) => {
      const nuc = new THREE.Mesh(new THREE.SphereGeometry(r, 22, 22), nucleusMats[i]);
      nuc.position.set(x, y, z);
      atom.add(nuc);
    });

    const orbitGroups = [];
    const orbitDefs = [
      { r: 0.72, tube: 0.022, color: INDIGO, speed: 0.9, tiltX: Math.PI / 2.6, rotY: 0 },
      { r: 0.95, tube: 0.02, color: FOREST, speed: -0.65, tiltX: Math.PI / 2.4, rotY: Math.PI / 3 },
      { r: 1.18, tube: 0.018, color: ORANGE, speed: 0.5, tiltX: Math.PI / 2.9, rotY: -Math.PI / 3 },
    ];
    orbitDefs.forEach((def) => {
      const orbit = new THREE.Group();
      orbit.rotation.x = def.tiltX;
      orbit.rotation.y = def.rotY;

      const ringMesh = new THREE.Mesh(
        new THREE.TorusGeometry(def.r, def.tube, 12, 80),
        new THREE.MeshStandardMaterial({ color: def.color, roughness: 0.35 }),
      );
      ringMesh.rotation.x = Math.PI / 2; // lay the torus flat in the XZ plane
      orbit.add(ringMesh);

      const electron = new THREE.Mesh(
        new THREE.SphereGeometry(0.09, 18, 18),
        new THREE.MeshStandardMaterial({ color: MINT, roughness: 0.25 }),
      );
      orbit.add(electron);

      atom.add(orbit);
      orbitGroups.push({ group: orbit, electron, radius: def.r, speed: def.speed });
    });

    atom.position.set(2.5, 3.0, -0.6);
    atom.userData = { baseY: 3.0, phase: 1.2, bobSpeed: 0.5 };
    world.add(atom);

    /* ---- Sparkle dots scattered around ---- */
    const dotMat = new THREE.MeshStandardMaterial({ color: 0xfdf8f0, roughness: 0.6 });
    const dots = [];
    for (let i = 0; i < 10; i += 1) {
      const s = 0.05 + Math.random() * 0.07;
      const dot = new THREE.Mesh(new THREE.SphereGeometry(s, 12, 12), dotMat);
      const angle = (i / 10) * Math.PI * 2;
      const radius = 4.2 + Math.random() * 1.1;
      dot.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius * 0.7, -1.2 - Math.random());
      dot.userData = { baseY: dot.position.y, phase: i * 0.9, bobSpeed: 0.9 };
      world.add(dot);
      dots.push(dot);
    }

    /* ---- Interaction & animation ---- */
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const pointerTarget = { x: 0, y: 0 };
    const pointerCurrent = { x: 0, y: 0 };

    function onPointerMove(e) {
      const rect = mount.getBoundingClientRect();
      pointerTarget.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointerTarget.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    }
    window.addEventListener("pointermove", onPointerMove);

    function resize() {
      width = mount.clientWidth || 800;
      height = mount.clientHeight || 600;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      // Pull the camera back on narrow containers so the whole
      // composition (objects out to |x| ~ 4.5) stays in frame.
      const vHalfTan = Math.tan(((43 / 2) * Math.PI) / 180);
      const zToFitWidth = 5.0 / (vHalfTan * camera.aspect);
      camera.position.z = Math.max(10.5, zToFitWidth);
      camera.updateProjectionMatrix();

      // Visible world bounds at the z=0 plane
      const halfH = vHalfTan * camera.position.z;
      const halfW = halfH * camera.aspect;

      // Pin the atom & pencil to the upper corners so they never
      // drift behind the floating quiz card.
      const atomX = -halfW + 1.65;
      const atomY = halfH - 1.15;
      atom.position.set(atomX, atomY, -0.6);
      atom.userData.baseY = atomY;

      const pencilX = halfW - 1.15;
      const pencilY = halfH - 1.5;
      pencil.position.set(pencilX, pencilY, -0.4);
      pencil.userData.baseY = pencilY;

      // Keep the answer cubes riding low, below the card zone
      const cubeBase = -(Math.min(halfH * 0.62, 3.1));
      const jitter = [0.18, -0.22, 0.08, -0.12];
      cubes.forEach((cube, i) => {
        cube.position.y = cubeBase + jitter[i % 4];
        cube.userData.baseY = cubeBase + jitter[i % 4];
      });
    }
    window.addEventListener("resize", resize);
    resize();

    const clock = new THREE.Clock();
    let rafId = 0;

    function tick() {
      const t = clock.getElapsedTime();

      world.children.forEach((obj) => {
        const u = obj.userData;
        if (!u || u.baseY === undefined) return;
        obj.position.y = u.baseY + Math.sin(t * u.bobSpeed + u.phase) * 0.14;
        if (u.rotSpeed) obj.rotation.y += u.rotSpeed;
        if (u.tumbleAxis) obj.rotateOnAxis(u.tumbleAxis, 0.004);
      });

      orbitGroups.forEach(({ electron, radius, speed }, i) => {
        const a = t * speed + i * 2.1;
        electron.position.set(Math.cos(a) * radius, 0, Math.sin(a) * radius);
      });

      // gentle parallax toward pointer
      pointerCurrent.x += (pointerTarget.x - pointerCurrent.x) * 0.04;
      pointerCurrent.y += (pointerTarget.y - pointerCurrent.y) * 0.04;
      world.rotation.y = pointerCurrent.x * 0.16;
      world.rotation.x = pointerCurrent.y * 0.1;

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    }

    if (reducedMotion) {
      renderer.render(scene, camera);
    } else {
      tick();
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("resize", resize);
      scene.traverse((obj) => {
        if (obj.isMesh) {
          obj.geometry.dispose();
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => {
            if (m.map) m.map.dispose();
            m.dispose();
          });
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
      aria-hidden="true"
    />
  );
}
